/**
 * Bank Accounts Step
 *
 * Third step - set up bank accounts with opening balances
 */

import { useEffect, useState } from 'react'

import { BANK_ACCOUNT_TYPES, BankAccountForm } from './BankAccountsStep-form'
import { useBaseCurrency } from '../../hooks/use-currencies'

import type { BankAccountInput } from '../../types/company-setup'

interface BankAccountsStepProps {
  bankAccounts: BankAccountInput[]
  onChange: (accounts: BankAccountInput[]) => void
  onNext: () => void
  onBack: () => void
}

const EMPTY_ACCOUNT: BankAccountInput = {
  name: '',
  accountType: 'current',
  bankName: '',
  branch: '',
  accountNumber: '',
  nameAtBank: '',
  ifscCode: '',
  swiftCode: '',
  iban: '',
  routingNumber: '',
  sortCode: '',
  currency: '',
  openingBalance: 0,
  openingDate: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}` })(),
}

interface InfoBannerProps {
  children: React.ReactNode
}

function InfoBanner({ children }: InfoBannerProps): JSX.Element {
  return (
    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path clipRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" fillRule="evenodd" />
        </svg>
        <div className="text-sm text-blue-700 dark:text-blue-300">{children}</div>
      </div>
    </div>
  )
}

interface BankAccountCardHeaderProps {
  accountName: string
  index: number
  isEditing: boolean
  onToggleEdit: () => void
  onRemove: () => void
}

function BankAccountCardHeader({
  accountName, index, isEditing, onToggleEdit, onRemove,
}: BankAccountCardHeaderProps): JSX.Element {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-medium text-foreground">{accountName || `Bank Account ${index + 1}`}</h3>
      <div className="flex items-center gap-2">
        <button className="text-sm text-primary hover:underline" type="button" onClick={onToggleEdit}>
          {isEditing ? 'Collapse' : 'Edit'}
        </button>
        <button className="text-sm text-red-600 dark:text-red-400 hover:underline" type="button" onClick={onRemove}>
          Remove
        </button>
      </div>
    </div>
  )
}

interface BankAccountSummaryProps {
  accountType: string
  bankName?: string
  currency: string
  openingBalance: number
}

function BankAccountSummary({ accountType, bankName, currency, openingBalance }: BankAccountSummaryProps): JSX.Element {
  const typeLabel = BANK_ACCOUNT_TYPES.find(t => t.value === accountType)?.label
  return (
    <div className="text-sm text-foreground-secondary space-y-1">
      <p><span className="font-medium">Type:</span> {typeLabel}</p>
      {bankName && <p><span className="font-medium">Bank:</span> {bankName}</p>}
      <p><span className="font-medium">Opening Balance:</span> {currency} {(openingBalance ?? 0).toFixed(2)}</p>
    </div>
  )
}

interface AccountCardProps {
  account: BankAccountInput
  index: number
  isEditing: boolean
  onToggleEdit: () => void
  onRemove: () => void
  onUpdate: (index: number, field: keyof BankAccountInput, value: string | number) => void
}

function AccountCard({ account, index, isEditing, onToggleEdit, onRemove, onUpdate }: AccountCardProps): JSX.Element {
  return (
    <div className={`border-2 rounded-lg p-4 transition-colors ${isEditing ? 'border-primary bg-muted' : 'border-border'}`}>
      <BankAccountCardHeader
        accountName={account.name ?? ''}
        index={index}
        isEditing={isEditing}
        onRemove={onRemove}
        onToggleEdit={onToggleEdit}
      />
      {isEditing ? (
        <BankAccountForm account={account} index={index} onUpdate={onUpdate} />
      ) : (
        <BankAccountSummary
          accountType={account.accountType ?? ''}
          bankName={account.bankName ?? ''}
          currency={account.currency ?? ''}
          openingBalance={account.openingBalance ?? 0}
        />
      )}
    </div>
  )
}

interface AccountListProps {
  bankAccounts: BankAccountInput[]
  editingIndex: number | null
  onToggleEdit: (i: number) => void
  onRemove: (i: number) => void
  onUpdate: (index: number, field: keyof BankAccountInput, value: string | number) => void
}

function AccountList({ bankAccounts, editingIndex, onToggleEdit, onRemove, onUpdate }: AccountListProps): JSX.Element {
  if (bankAccounts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p className="mb-4">No bank accounts added yet.</p>
        <p className="text-sm">Click &quot;Add Bank Account&quot; below to get started, or skip this step if you prefer to add accounts later.</p>
      </div>
    )
  }
  return (
    <>
      {bankAccounts.map((account, index) => (
        <AccountCard
          key={index}
          account={account}
          index={index}
          isEditing={editingIndex === index}
          onRemove={(): void => onRemove(index)}
          onToggleEdit={(): void => onToggleEdit(index)}
          onUpdate={onUpdate}
        />
      ))}
    </>
  )
}

interface ActionBarProps {
  onBack: () => void
  onNext: () => void
}

function ActionBar({ onBack, onNext }: ActionBarProps): JSX.Element {
  return (
    <div className="flex justify-between pt-4">
      <button
        className="px-3 py-2 border border-border text-foreground-secondary rounded-lg hover:bg-surface-secondary transition-colors font-medium"
        type="button"
        onClick={onBack}
      >
        Back
      </button>
      <button
        className="px-3 py-2 bg-primary-solid text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
        type="button"
        onClick={onNext}
      >
        Next: Owner Equity
      </button>
    </div>
  )
}

interface UseBankAccountsStateReturn {
  editingIndex: number | null
  handleAddAccount: () => void
  handleRemoveAccount: (index: number) => void
  handleUpdateAccount: (index: number, field: keyof BankAccountInput, value: string | number) => void
  handleToggleEdit: (index: number) => void
}

function useBankAccountsState(
  bankAccounts: BankAccountInput[],
  onChange: (a: BankAccountInput[]) => void,
  baseCurrency: string,
): UseBankAccountsStateReturn {
  const shouldAutoExpand = bankAccounts.length === 0 ||
    (bankAccounts.length === 1 && (!bankAccounts[0]?.name || bankAccounts[0]?.name.trim() === ''))
  const [editingIndex, setEditingIndex] = useState<number | null>(shouldAutoExpand ? 0 : null)

  useEffect(() => {
    if (bankAccounts.length === 0) {
      onChange([{ ...EMPTY_ACCOUNT, currency: baseCurrency }])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAddAccount = (): void => {
    onChange([...bankAccounts, { ...EMPTY_ACCOUNT, currency: baseCurrency }])
    setEditingIndex(bankAccounts.length)
  }

  const handleRemoveAccount = (index: number): void => {
    onChange(bankAccounts.filter((_, i) => i !== index))
    setEditingIndex(null)
  }

  const handleUpdateAccount = (index: number, field: keyof BankAccountInput, value: string | number): void => {
    const newAccounts = [...bankAccounts]
    newAccounts[index] = { ...newAccounts[index], [field]: value }
    onChange(newAccounts)
  }

  const handleToggleEdit = (index: number): void => {
    setEditingIndex(editingIndex === index ? null : index)
  }

  return { editingIndex, handleAddAccount, handleRemoveAccount, handleUpdateAccount, handleToggleEdit }
}

export function BankAccountsStep({ bankAccounts, onChange, onNext, onBack }: BankAccountsStepProps): JSX.Element {
  const baseCurrency = useBaseCurrency()
  const { editingIndex, handleAddAccount, handleRemoveAccount, handleUpdateAccount, handleToggleEdit } =
    useBankAccountsState(bankAccounts, onChange, baseCurrency)

  const handleNext = (): void => {
    const validAccounts = bankAccounts.filter(acc => acc.name?.trim() !== '')
    onChange(validAccounts)
    void setTimeout(() => onNext(), 0)
  }

  return (
    <div className="space-y-6">
      <InfoBanner>
        <p className="font-medium mb-1">Set Up Your Bank Accounts</p>
        <p>Add all your business bank accounts and their current balances. This helps us create accurate opening balance entries. You can skip this and add accounts later if needed.</p>
      </InfoBanner>
      <div className="space-y-4">
        <AccountList
          bankAccounts={bankAccounts}
          editingIndex={editingIndex}
          onRemove={handleRemoveAccount}
          onToggleEdit={handleToggleEdit}
          onUpdate={handleUpdateAccount}
        />
      </div>
      <button
        className="w-full py-3 border-2 border-dashed border-border rounded-lg text-foreground-secondary hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
        type="button"
        onClick={handleAddAccount}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
        {bankAccounts.length === 0 ? 'Add Bank Account' : 'Add Another Bank Account'}
      </button>
      <ActionBar onBack={onBack} onNext={handleNext} />
    </div>
  )
}
